import { Globe, TrendingDown, Trophy } from "lucide-react";
import { memo } from "react";
import { formatToCr } from "src/helpers/api/sharedFunction";
import {
  formatCurrencyWithLocalString,
  formatPercentageWithLocalString,
} from "src/helpers/numberFormatter";
import InfoModalSkeleton from "../skeleton/InfoModalSkeleton";

interface InfoModalProps {
  companyInfo: Record<string, string> | undefined;
  loading: boolean;
  /** Single scroll layout when nested in AnalyzeStockModal */
  compact?: boolean;
}

type LevelItem = {
  label: string;
  value: string;
  level?: string | number;
  icon: string;
};

const InfoModal = ({
  companyInfo,
  loading,
  compact = false,
}: InfoModalProps) => {
  const getRewardOrRiskValue = (value?: string) => {
    if (!value || value === "-") return "-";
    return formatPercentageWithLocalString(Number(value));
  };

  const sortLevelsAsc = (arr: LevelItem[]) =>
    [...arr].sort((a, b) => Number(a.level ?? 999) - Number(b.level ?? 999));

  const rewardLevels =
    companyInfo &&
    sortLevelsAsc([
      {
        label: "Market Segment",
        value: companyInfo.tgt_bymarketsegment,
        level: companyInfo.tgt_market_level,
        icon: "market",
      },
      {
        label: "ATR",
        value: getRewardOrRiskValue(companyInfo.atr_tgt_percent),
        level: companyInfo.atr_target_level,
        icon: "atr",
      },
      {
        label: "Sector + Segment",
        value: getRewardOrRiskValue(companyInfo.tgt_sector_marketsegment_perc),
        level: companyInfo.sector_target_level,
        icon: "sector",
      },
    ]);

  const riskLevels =
    companyInfo &&
    sortLevelsAsc([
      {
        label: "Market Segment",
        value: companyInfo.sl_bymarketsegment,
        level: companyInfo.sl_market_level,
        icon: "market",
      },
      {
        label: "ATR",
        value: getRewardOrRiskValue(companyInfo.atr_sl_percent),
        level: companyInfo.atr_sl_level,
        icon: "atr",
      },
      {
        label: "Sector + Segment",
        value: getRewardOrRiskValue(companyInfo.sl_sector_marketsegment_perc),
        level: companyInfo.sector_sl_level,
        icon: "sector",
      },
    ]);

  const rewardRiskBlocks = (
    <>
      <div className="bg-white/90 dark:bg-gray-900/90 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 font-figtree mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-6 h-6 text-amber-500" />
          <span className="text-sm font-medium text-amber-500 dark:text-amber-500">
            Reward Reference Levels as per:
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3 text-sm">
          {rewardLevels?.map((item, i) => (
            <div
              key={i}
              className="flex relative flex-col px-2 py-3 rounded-lg bg-gradient-to-br from-[#f4f6fb] to-[#e9ebf3] dark:from-[#2a2d3e] dark:to-[#1f2230] shadow-sm mt-2 sm:mt-0"
            >
              <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                {item.label}
              </span>
              <span className="text-green-600 dark:text-green-500 font-medium">
                {item.value}
              </span>
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs px-3 py-[1px] rounded-md bg-white dark:bg-[#1E1E2D] text-green-600 dark:text-green-400 font-semibold border border-green-200 dark:border-green-700 shadow-sm">
                Level {item.level ?? "-"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white/90 dark:bg-gray-900/90 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 font-figtree mb-4">
        <div className="flex items-center gap-2 mb-4">
          <TrendingDown className="w-6 h-6 text-red-400" />
          <span className="text-sm font-medium text-red-400 dark:text-red-400">
            Risk Reference Levels as per:
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3 text-sm">
          {riskLevels?.map((item, i) => (
            <div
              key={i}
              className="flex relative flex-col px-2 py-3 rounded-lg bg-gradient-to-br from-[#f4f6fb] to-[#e9ebf3] dark:from-[#2a2d3e] dark:to-[#1f2230] shadow-sm mt-2 sm:mt-0"
            >
              <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                {item.label}
              </span>
              <span className="text-red-500 dark:text-red-500 font-medium">
                {item.value}
              </span>
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs px-3 py-[1px] rounded-md bg-white dark:bg-[#1E1E2D] text-red-500 dark:text-red-400 font-semibold border border-red-200 dark:border-red-700 shadow-sm">
                Level {item.level ?? "-"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-indigo-50/50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800/30 p-3 mt-1 font-figtree">
        <p className="text-sm font-medium text-indigo-700 dark:text-indigo-400 mb-1.5">
          Investment Timeframes & Levels:
        </p>
        <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1 list-disc pl-4 leading-snug">
          <li>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              Under 1 Month:
            </span>{" "}
            Use ATR (Average True Range) to gauge immediate volatility.
          </li>
          <li>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              1 to 3 Months:
            </span>{" "}
            Use Market Segment levels, factoring in specific stock volatility
            and segment behavior.
          </li>
          <li>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              Over 3 Months:
            </span>{" "}
            Use Sector + Segment levels, incorporating sector rotation, segment
            trends, and volatility.
          </li>
        </ul>
      </div>
    </>
  );

  const companyDetails = companyInfo && (
    <>
      <div className="flex flex-col flex-shrink-0 min-w-0 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl dark:text-white font-bold leading-tight truncate">
          {companyInfo.name}
        </h2>
        <div className="text-sm font-normal text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2 flex-wrap">
          <span className="font-medium">
            Price:{" "}
            <span className="font-medium text-gray-800 dark:text-gray-200">
              {formatCurrencyWithLocalString(companyInfo.price)}
            </span>
          </span>
          <span className="hidden sm:inline-block text-gray-300 dark:text-gray-600">
            |
          </span>
          {companyInfo.market_cap && (
            <span className="font-medium">
              Market Cap:{" "}
              <span className="text-gray-800 dark:text-gray-200">
                {formatToCr(Number(companyInfo.market_cap))}
              </span>
            </span>
          )}
          <span className="hidden sm:inline-block text-gray-300 dark:text-gray-600">
            |
          </span>
          <span className="font-medium">
            Segment:{" "}
            <span className="text-gray-800 dark:text-gray-200">
              {companyInfo.segment}
            </span>
          </span>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex-shrink-0">
        {companyInfo.company_name}
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Sector</p>
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
              {companyInfo.sector || "N/A"}
            </p>

            {companyInfo?.trending_sector_text && (
              <span className="inline-flex items-center whitespace-nowrap px-2 py-[2px] rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" />
                {companyInfo.trending_sector_text}
              </span>
            )}
          </div>
        </div>
        <div className="ml-auto">
          <p className="text-xs text-gray-500 dark:text-gray-400">Industry</p>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {companyInfo.industry || "N/A"}
          </p>
        </div>
      </div>
    </>
  );

  const riskNote = (
    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md flex-shrink-0 mt-2 mr-2">
      <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
        Risk Note:
      </p>
      <p className="text-xs text-amber-700 dark:text-amber-300 ">
        These are model reference levels derived from volatility & sector
        analysis. They are provided for educational and research purposes only
        and are not investment recommendations. Please consult a SEBI-registered
        adviser before making investment decisions.
      </p>
    </div>
  );

  return (
    <div className="bg-transparent w-full h-full flex flex-col overflow-hidden">
      {loading ? (
        <InfoModalSkeleton />
      ) : (
        companyInfo &&
        (compact ? (
          <div className="min-h-0 flex-1 overflow-y-auto custom-scroll p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col min-w-0">
                {companyDetails}
                <div className="mb-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Description
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed text-justify">
                    {companyInfo.long_business_summary}
                  </p>
                </div>
              </div>
              <div className="border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 pt-6 md:pt-0 md:pl-6 flex flex-col">
                {rewardRiskBlocks}
                {riskNote}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-6 rounded-lg flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 min-h-0">
              {/* Part 1: Company Details */}
              <div className="flex flex-col min-h-0">
                <div className="flex flex-col flex-shrink-0 min-w-0 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl dark:text-white font-bold leading-tight truncate">
                    {companyInfo.name}
                  </h2>
                  <div className="text-sm font-normal text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2 flex-wrap">
                    <span className="font-medium">
                      Price:{" "}
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {formatCurrencyWithLocalString(companyInfo.price)}
                      </span>
                    </span>
                    <span className="hidden sm:inline-block text-gray-300 dark:text-gray-600">
                      |
                    </span>
                    {companyInfo.market_cap && (
                      <span className="font-medium">
                        Market Cap:{" "}
                        <span className="text-gray-800 dark:text-gray-200">
                          {formatToCr(Number(companyInfo.market_cap))}
                        </span>
                      </span>
                    )}
                    <span className="hidden sm:inline-block text-gray-300 dark:text-gray-600">
                      |
                    </span>
                    <span className="font-medium">
                      Segment:{" "}
                      <span className="text-gray-800 dark:text-gray-200">
                        {companyInfo.segment}
                      </span>
                    </span>
                    {companyInfo.website && (
                      <>
                        <span className="hidden sm:inline-block text-gray-300 dark:text-gray-600">
                          |
                        </span>
                        <a
                          href={companyInfo.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex items-center gap-1 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Globe className="w-3.5 h-3.5" />
                          Website
                        </a>
                      </>
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex-shrink-0">
                  {companyInfo.company_name}
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Sector
                    </p>
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
                        {companyInfo.sector || "N/A"}
                      </p>

                      {companyInfo?.trending_sector_text && (
                        <span className="inline-flex items-center whitespace-nowrap px-2 py-[2px] rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" />
                          {companyInfo.trending_sector_text}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Industry
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {companyInfo.industry || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="mb-2 overflow-y-auto">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Description
                  </p>
                  <div className="overflow-y-auto custom-scroll pr-2 max-h-[380px]">
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed text-justify">
                      {companyInfo.long_business_summary}
                    </p>
                  </div>
                </div>
              </div>

              {/* Part 2: Risk, Reward, and Note */}
              <div className="border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 pt-6 md:pt-0 md:pl-6 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto custom-scroll pr-2 min-h-0 mb-2">
                  {rewardRiskBlocks}
                </div>
                {riskNote}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default memo(InfoModal);
